import classnames from 'classnames';
import type { CSSProperties } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { css, keyframes } from '@sorare/css';

import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { glossary } from '#src/lib/glossary.ts';

import loadingGif from './loading.gif';

export interface Props {
  error?: string;
  style?: CSSProperties;
  small?: boolean;
  smaller?: boolean;
  fullHeight?: boolean;
  grow?: boolean;
  fullScreen?: boolean;
}

const Container = styled.div<{ $white?: boolean }>`
  position: relative;
  color: inherit;
  width: 1em;
  height: 1em;
  overflow: hidden;
  border-radius: 1em;
`;

const starAnimation = keyframes`
  0% {
    transform: rotate(-10deg) scale(1.15);
  }
  30% {
    transform: rotate(-92deg) scale(1);
  }
  100% {
    transform: rotate(-72deg) scale(1.15);
  }
`;

// Blink does not composite this transform animation while it is declared on the <svg>:
// it recalculates style and pre-paints every frame instead, measured at 120/s against
// 5/s once a wrapping HTML div carries the animation. Related: crbug.com/1186312.
const spinner = css`
  animation: ${starAnimation} 1.35s linear infinite;
`;

const Svg = styled.svg`
  display: block;
  width: 100%;
  height: 100%;
`;

export const BaseLoadingIndicator = () => {
  const { formatMessage } = useIntl();
  return (
    <Container aria-label={formatMessage(glossary.loading)}>
      <div className={spinner}>
        <Svg
          width="60"
          height="60"
          viewBox="0 0 34 34"
          fill="none"
          stroke="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="17" cy="17" r="14.5" strokeWidth={5} />
          <path
            d="M22.22 14.04Q19.98 17.34 21.43 21.05Q17.60 19.94 14.51 22.46Q14.39 18.48 11.04 16.32Q14.79 14.97 15.80 11.12Q18.24 14.27 22.22 14.04Z"
            strokeLinejoin="round"
            strokeWidth={1}
            fill="currentColor"
          />
        </Svg>
      </div>
    </Container>
  );
};

const Root = styled(Horizontal).attrs({ gap: 0, center: true })`
  background-color: transparent;
  width: 100%;
  height: 100%;
  margin: auto;
  vertical-align: top;
  &.fullHeight {
    height: 100vh;
  }
  &.fullHeight.white {
    background-color: var(--c-white);
  }
  &.grow {
    flex: 1;
  }
  &.fullScreen {
    position: fixed;
    inset: 0;
    overflow: auto;
    z-index: 10;
    background: var(--c-black);
  }
`;
const Error = styled.span``;
const StyledContainer = styled.span`
  position: relative;
  font-size: calc(8 * var(--unit));
  ${Root}.small & {
    font-size: var(--quadruple-unit);
  }
  ${Root}.smaller & {
    font-size: var(--double-unit);
  }
  & img {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

export const LoadingIndicator = ({
  error,
  style,
  small,
  smaller,
  fullHeight,
  fullScreen,
  grow,
}: Props) => {
  const { formatMessage } = useIntl();
  const { isMobileApp } = useIsMobileApp();

  return (
    <Root
      className={classnames({
        small,
        smaller,
        fullHeight,
        fullScreen,
        grow,
      })}
      style={style}
    >
      {error ? (
        <Error>{error}</Error>
      ) : (
        <StyledContainer>
          {isMobileApp ? (
            <Container aria-label={formatMessage(glossary.loading)}>
              <img src={loadingGif} alt={formatMessage(glossary.loading)} />
            </Container>
          ) : (
            <BaseLoadingIndicator />
          )}
        </StyledContainer>
      )}
    </Root>
  );
};

type LoadingIndicatorPlaceholderProps = Pick<
  Props,
  'small' | 'smaller' | 'fullHeight' | 'grow'
>;

export const LoadingIndicatorPlaceholder = ({
  small,
  smaller,
  fullHeight,
  grow,
}: LoadingIndicatorPlaceholderProps) => (
  <Root
    className={classnames({ small, smaller, fullHeight, grow })}
    aria-hidden
  >
    <StyledContainer>
      <Container />
    </StyledContainer>
  </Root>
);
